import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Briefcase, Camera, Loader2, Globe, LogOut, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ProfileSettings() {
  const { user, refetchUser, logout } = useAuth();
  const { lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user && isOpen) {
      setName(user.name || '');
      setTitle(user.title || '');
      setAvatarPreview(user.avatar || null);
    }
  }, [user, isOpen]);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Bilden får max vara 2 MB');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;
        setAvatarPreview(base64);
        
        await axios.patch(`${API}/auth/me`, {
          avatar: base64
        }, { withCredentials: true });
        
        await refetchUser();
        toast.success('Profilbild uppdaterad');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Kunde inte ladda upp bilden');
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Namn får inte vara tomt');
      return;
    }

    setLoading(true);
    try {
      await axios.patch(`${API}/auth/me`, {
        name: name.trim(),
        title: title.trim()
      }, { withCredentials: true });
      
      await refetchUser();
      toast.success('Profil uppdaterad');
      setIsOpen(false);
    } catch (error) {
      toast.error('Kunde inte uppdatera profil');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  const avatarSrc = avatarPreview || user?.avatar;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <button
          className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors text-left"
          data-testid="profile-settings-btn"
        >
          {avatarSrc ? (
            <img src={avatarSrc} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs text-zinc-300">
              {user.name?.[0]?.toUpperCase() || 'U'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-100 truncate">{user.name || 'Användare'}</p>
            <p className="text-xs text-zinc-500 truncate">{user.title || user.email}</p>
          </div>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#141416] border-white/10 text-zinc-50 max-w-sm p-0 gap-0" data-testid="profile-settings-modal">
        <DialogHeader className="px-5 pt-5 pb-0">
          <DialogTitle className="text-base font-medium font-['Outfit'] flex items-center gap-2">
            <User className="w-4 h-4" />
            Redigera profil
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[70vh] px-5 py-4 space-y-4">
          {/* Avatar Upload */}
          <div className="flex items-center gap-4">
            <div className="relative group flex-shrink-0">
              {avatarSrc ? (
                <img src={avatarSrc} alt="" className="w-14 h-14 rounded-full object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-zinc-800 flex items-center justify-center text-lg text-zinc-300 font-medium">
                  {name?.[0]?.toUpperCase() || 'U'}
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                data-testid="avatar-upload-btn"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 text-white" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleAvatarUpload}
                className="hidden"
                data-testid="avatar-file-input"
              />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-zinc-100 truncate">{user.name || 'Användare'}</p>
              <p className="text-xs text-zinc-500 truncate">{user.email}</p>
            </div>
          </div>

          {/* Name */}
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-xs">Namn</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ditt namn"
              className="bg-[#09090B] border-white/10 text-zinc-100 focus:border-zinc-500 h-9 text-sm"
              data-testid="profile-name-input"
            />
          </div>

          {/* Title/Role */}
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-xs">Roll / Titel</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="t.ex. Projektledare, VD"
              className="bg-[#09090B] border-white/10 text-zinc-100 focus:border-zinc-500 h-9 text-sm"
              data-testid="profile-title-input"
            />
          </div>

          {/* Language */}
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-xs flex items-center gap-1.5">
              <Globe className="w-3 h-3" />
              Språk
            </Label>
            <div className="flex gap-2" data-testid="profile-language-switcher">
              <button
                onClick={() => setLang('sv')}
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  lang === 'sv'
                    ? 'bg-white/10 text-zinc-100 border border-white/10'
                    : 'bg-[#09090B] text-zinc-500 border border-white/5 hover:text-zinc-300'
                }`}
                data-testid="profile-lang-sv"
                type="button"
              >
                Svenska
              </button>
              <button
                onClick={() => setLang('en')}
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  lang === 'en'
                    ? 'bg-white/10 text-zinc-100 border border-white/10'
                    : 'bg-[#09090B] text-zinc-500 border border-white/5 hover:text-zinc-300'
                }`}
                data-testid="profile-lang-en"
                type="button"
              >
                English
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              onClick={() => setIsOpen(false)}
              className="flex-1 border-white/10 text-zinc-400 hover:bg-white/5 hover:text-zinc-100 h-9 text-sm"
              data-testid="profile-cancel-btn"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading || !name.trim()}
              className="flex-1 bg-zinc-100 text-zinc-900 hover:bg-white h-9 text-sm"
              data-testid="profile-save-btn"
            >
              {loading ? 'Sparar...' : 'Spara'}
            </Button>
          </div>

          {/* Divider + Admin & Logout */}
          <div className="border-t border-white/5 pt-3 space-y-1">
            {user?.role === 'admin' && (
              <button
                onClick={() => { setIsOpen(false); navigate('/admin'); }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-zinc-500 hover:bg-white/5 hover:text-zinc-300 transition-colors"
                data-testid="admin-link"
              >
                <Shield className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">Admin</span>
              </button>
            )}
            <button
              onClick={() => { setIsOpen(false); logout(); }}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-zinc-500 hover:bg-white/5 hover:text-red-400 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-sm">Logga ut</span>
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
